import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react'
import { useSession } from './SessionContext'
import { UserRoles } from '../constants/userRoles'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '../firebase'

interface CorporateUser {
  id: string
  email: string
  displayName?: string
  role: string
}

interface CorporateFilterContextType {
  corporateUsers: CorporateUser[]
  selectedCorporation: string
  corporateLoading: boolean
  getSelectedCorporationData: () => CorporateUser | null
  setSelectedCorporation: (corporationId: string) => void
}

interface CorporateFilterProviderProps {
  children: ReactNode
}

// Create the context
const CorporateFilterContext = createContext<
  CorporateFilterContextType | undefined
>(undefined)

// Custom hook to use the corporate filter context
// eslint-disable-next-line react-refresh/only-export-components
export const useCorporateFilter = () => {
  const context = useContext(CorporateFilterContext)
  if (context === undefined) {
    throw new Error(
      'useCorporateFilter must be used within a CorporateFilterProvider'
    )
  }
  return context
}

// Storage key for persistence
const CORPORATE_FILTER_KEY = 'mntnd_selected_corporation'

// Provider component with built-in state management
export const CorporateFilterProvider = ({
  children,
}: CorporateFilterProviderProps) => {
  const { hasRole } = useSession()

  // Corporate filtering state with localStorage persistence
  const [corporateUsers, setCorporateUsers] = useState<CorporateUser[]>([])
  const [selectedCorporation, setSelectedCorporationState] = useState(() => {
    // Initialize from localStorage if available
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CORPORATE_FILTER_KEY) || 'all'
    }
    return 'all'
  })
  const [corporateLoading, setCorporateLoading] = useState(true)

  // Persistent setter that updates localStorage
  const setSelectedCorporation = (corporationId: string) => {
    setSelectedCorporationState(corporationId)
    if (typeof window !== 'undefined') {
      localStorage.setItem(CORPORATE_FILTER_KEY, corporationId)
    }
  }

  // Fetch corporate users for SUPER_ADMIN
  useEffect(() => {
    const fetchCorporateUsers = async () => {
      if (!hasRole(UserRoles.SUPER_ADMIN)) {
        setCorporateLoading(false)
        return
      }

      try {
        const corporateQuery = query(
          collection(db, 'users'),
          where('role', '==', UserRoles.CORPORATE)
        )
        const querySnapshot = await getDocs(corporateQuery)
        const users = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as CorporateUser[]

        setCorporateUsers(users)
      } catch (error) {
        console.error('Error fetching corporate users:', error)
      } finally {
        setCorporateLoading(false)
      }
    }

    fetchCorporateUsers()
  }, [hasRole])

  // Get selected corporation data
  const getSelectedCorporationData = () => {
    if (selectedCorporation === 'all') {
      return null
    }

    return (
      corporateUsers.find(user => user.email === selectedCorporation) || null
    )
  }

  const value = {
    corporateUsers,
    selectedCorporation,
    corporateLoading,
    getSelectedCorporationData,
    setSelectedCorporation,
  }

  return (
    <CorporateFilterContext.Provider value={value}>
      {children}
    </CorporateFilterContext.Provider>
  )
}
